import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Server, Plug, FlaskConical } from 'lucide-react';
import { getMcpTools } from '../../services/mcpApi';
import McpMessageBar from './McpMessageBar';
import McpIntegrationTab from './McpIntegrationTab';
import McpPlaygroundTab from './McpPlaygroundTab';

const TABS = [
  { id: 'integration', label: 'Integration', icon: Plug },
  { id: 'playground', label: 'Playground', icon: FlaskConical },
];

export default function McpServerPage() {
  const [activeTab, setActiveTab] = useState('integration');
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  const notify = useCallback((tone, text) => {
    setMessage(text ? { tone, text } : null);
  }, []);

  const loadCatalog = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getMcpTools();
      setCatalog(data);
    } catch (err) {
      notify('danger', `Could not load MCP tool catalog: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  const tools = useMemo(() => catalog?.tools || [], [catalog]);

  return (
    <div className="d-flex flex-column" style={{ minHeight: '100%' }}>
      <McpMessageBar message={message} onClose={() => setMessage(null)} />

      <div className="bg-white border-bottom px-3 px-md-4 py-3 sticky-top" style={{ top: 0, zIndex: 5 }}>
        <div className="d-flex align-items-center gap-2">
          <Server size={22} className="text-primary" />
          <div>
            <h1 className="h5 mb-0">MCP Server</h1>
            <div className="text-muted small">
              ExtremeB2B OrderX tools exposed over the Model Context Protocol
            </div>
          </div>
        </div>
        <ul className="nav nav-tabs border-0 mt-3" role="tablist">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <li className="nav-item" key={tab.id} role="presentation">
                <button
                  type="button"
                  role="tab"
                  aria-selected={active}
                  className={`nav-link d-flex align-items-center gap-1 ${active ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <Icon size={15} />
                  {tab.label}
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="container-fluid px-3 px-md-4 py-3 flex-grow-1">
        {loading ? (
          <div className="text-center text-muted py-5">
            <div className="spinner-border text-primary" role="status" aria-hidden="true" />
            <div className="mt-2">Loading MCP catalog…</div>
          </div>
        ) : activeTab === 'integration' ? (
          <McpIntegrationTab catalog={catalog} tools={tools} notify={notify} />
        ) : (
          <McpPlaygroundTab tools={tools} notify={notify} />
        )}
      </div>
    </div>
  );
}
